import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Modal,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import toast from "react-hot-toast";
import { Triangle } from "react-loader-spinner";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CampaignService from "../../../services/OrderingServices/CampaignService";
import AdsService from "../../../services/OrderingServices/AdsService";

const CampaignForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [adsList, setAdsList] = useState([]);
  const flatpickrRef = useRef(null);
  const [actionType, setActionType] = useState("save");
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [campaignId, setCampaignId] = useState(null);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Campaign name is required"),
    description: Yup.string(),
    dateRange: Yup.array()
      .of(Yup.date())
      .min(2, "Start and End date are required")
      .required("Date range is required"),

    isActive: Yup.boolean(),
    adIds: Yup.array(),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      startDate: null,
      endDate: null,
      playUntilTurnedOff: false,
      isDraft: true,
      isActive: true,
      tags: [],
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required("Campaign name is required"),
      description: Yup.string(),
      startDate: Yup.date().nullable(),
      endDate: Yup.date()
        .nullable()
        .min(Yup.ref("startDate"), "End date cannot be before start date"),
      playUntilTurnedOff: Yup.boolean(),
      isDraft: Yup.boolean(),
      isActive: Yup.boolean(),
      tags: Yup.array().of(Yup.string()),
    }),
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        const submitData = {
          name: values.name,
          description: values.description,
          playUntilTurnedOff: values.playUntilTurnedOff,
          isActive: values.isActive,
          isDraft: true,
          tags: values.tags,
        };

        if (values.startDate) {
          submitData.startDate = new Date(values.startDate).toISOString();
        }

        if (values.endDate) {
          submitData.endDate = new Date(values.endDate).toISOString();
        }

        let response;
        let isNewCampaign = !id || id === "new";
        if (id && id !== "new") {
          response = await CampaignService.update(id, submitData);
          toast.success("Campaign updated successfully");
          setCampaignId(id);
          setShowDraftModal(true);
        } else {
          response = await CampaignService.create(submitData);
          toast.success("Campaign created successfully");
          const newCampaignId =
            response?.data?.id || response?.data || response;
          navigate(`/campaign/view/${newCampaignId}`, {
            state: { openMediaModal: isNewCampaign },
          });
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to save campaign",
        );
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    fetchAdsList();
    if (id && id !== "new") {
      fetchCampaignDetail();
    }
  }, [id]);

  async function fetchAdsList() {
    try {
      const response = await AdsService.adsList({ pageSize: 100 });
      let ads = [];
      if (Array.isArray(response)) {
        ads = response;
      } else if (response?.data && Array.isArray(response.data)) {
        ads = response.data;
      } else if (response?.value && Array.isArray(response.value)) {
        ads = response.value;
      }
      setAdsList(ads);
    } catch (error) {
      toast.error("Failed to fetch ads list");
      console.error(error);
    }
  }

  async function fetchCampaignDetail() {
    try {
      setLoading(true);
      const response = await CampaignService.getCampaignById(id);
      if (response) {
        const campaignData = response.data || response;
        formik.setValues({
          name: campaignData.name || "",
          description: campaignData.description || "",
          dateRange: [
            campaignData.startDate ? new Date(campaignData.startDate) : null,
            campaignData.endDate ? new Date(campaignData.endDate) : null,
          ],
          isActive:
            campaignData.isActive !== undefined ? campaignData.isActive : true,
          adIds: Array.isArray(campaignData.adIds) ? campaignData.adIds : [],
        });
      }
    } catch (error) {
      toast.error("Failed to fetch campaign details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleCancel = () => {
    navigate("/campaign");
  };

  const handleAdToggle = (adId) => {
    const currentAdIds = formik.values.adIds || [];
    if (currentAdIds.includes(adId)) {
      formik.setFieldValue(
        "adIds",
        currentAdIds.filter((id) => id !== adId),
      );
    } else {
      formik.setFieldValue("adIds", [...currentAdIds, adId]);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Triangle color="#435ebe" height={80} width={80} />
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb
            title={id && id !== "new" ? "Edit Campaign" : "New Campaign"}
            pageTitle="Campaigns"
          />
        </div>
        <Container fluid>
          <Row>
            <Col lg="10" className="mx-auto">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light">
                  <h5 className="mb-0">
                    {id && id !== "new"
                      ? "Edit Campaign Information"
                      : "Add New Campaign"}
                  </h5>
                </CardHeader>
                <CardBody className="p-4">
                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      {/* Column 1 */}
                      <Col md="6">
                        {/* Campaign Name */}
                        <FormGroup className="mb-3">
                          <Label htmlFor="name" className="fw-bold">
                            Campaign Name
                            <span style={{ color: "red" }}>*</span>
                          </Label>
                          <Input
                            type="text"
                            name="name"
                            id="name"
                            placeholder="Enter campaign name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!(formik.touched.name && formik.errors.name)
                            }
                          />
                          {formik.touched.name && formik.errors.name && (
                            <FormFeedback>{formik.errors.name}</FormFeedback>
                          )}
                        </FormGroup>

                        {/* Active Status */}
                        <FormGroup className="mb-3">
                          <Label htmlFor="isActive" className="fw-bold">
                            Status
                          </Label>
                          <Input
                            type="select"
                            name="isActive"
                            id="isActive"
                            value={formik.values.isActive ? "true" : "false"}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "isActive",
                                e.target.value === "true",
                              )
                            }
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </Input>
                        </FormGroup>

                        {/* Date Range */}
                        <FormGroup className="mb-3">
                          <Label className="fw-bold">
                            Campaign Date Range
                            <span style={{ color: "red" }}>*</span>
                          </Label>

                          <Flatpickr
                            value={[
                              formik.values.startDate,
                              formik.values.endDate,
                            ]}
                            onChange={(dates) => {
                              formik.setFieldValue(
                                "startDate",
                                dates[0] || null,
                              );
                              formik.setFieldValue("endDate", dates[1] || null);
                            }}
                            options={{
                              mode: "range",
                              enableTime: true,
                              dateFormat: "Y-m-d H:i",
                            }}
                            className="form-control"
                          />
                          {(formik.touched.startDate &&
                            formik.errors.startDate) ||
                          (formik.touched.endDate && formik.errors.endDate) ? (
                            <div className="text-danger small mt-1">
                              {formik.errors.startDate || formik.errors.endDate}
                            </div>
                          ) : null}

                          {formik.touched.dateRange &&
                            formik.errors.dateRange && (
                              <div className="text-danger small mt-1">
                                {formik.errors.dateRange}
                              </div>
                            )}
                        </FormGroup>

                        <FormGroup check className="mb-2">
                          <Input
                            type="checkbox"
                            id="playUntilTurnedOff"
                            checked={formik.values.playUntilTurnedOff}
                            onChange={() =>
                              formik.setFieldValue(
                                "playUntilTurnedOff",
                                !formik.values.playUntilTurnedOff,
                              )
                            }
                          />
                          <Label for="playUntilTurnedOff" check>
                            Play Until Turned Off
                          </Label>
                        </FormGroup>

                        {/* <FormGroup check className="mb-2">
                          <Input
                            type="checkbox"
                            id="isDraft"
                            checked={formik.values.isDraft}
                            onChange={() =>
                              formik.setFieldValue(
                                "isDraft",
                                !formik.values.isDraft,
                              )
                            }
                          />
                          <Label for="isDraft" check>
                            Save as Draft
                          </Label>
                        </FormGroup> */}
                      </Col>

                      {/* Column 2 */}
                      <Col md="6">
                        {/* Description */}
                        <FormGroup className="mb-3">
                          <Label htmlFor="description" className="fw-bold">
                            Description
                          </Label>
                          <Input
                            type="textarea"
                            name="description"
                            id="description"
                            placeholder="Enter campaign description"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            rows="9"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Action Buttons */}
                    <Row className="mt-4">
                      <Col className="d-flex gap-2 justify-content-end">
                        <Button
                          color="secondary"
                          outline
                          type="button"
                          onClick={handleCancel}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>

                        {id && id !== "new" ? (
                          <Button
                            color="primary"
                            type="submit"
                            disabled={submitting}
                          >
                            Update
                          </Button>
                        ) : (
                          <Button
                            color="primary"
                            type="submit"
                            disabled={submitting}
                            onClick={() => setActionType("assign")}
                          >
                            Assign Media
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Draft Modal */}
      <Modal isOpen={showDraftModal} centered>
        <ModalBody className="text-center py-4">
          <div className="mb-3">
            <i
              className="ri-information-line"
              style={{ fontSize: "3rem", color: "#435ebe" }}
            ></i>
          </div>
          <h5 className="mb-2">Campaign Updated Successfully</h5>
          <p className="text-muted mb-4">
            Your campaign has been saved as a draft. You can now publish it or
            reschedule for a later date.
          </p>
        </ModalBody>
        <ModalFooter className="justify-content-center gap-2 border-top">
          <Button
            color="secondary"
            outline
            onClick={() => {
              setShowDraftModal(false);
              navigate("/campaign");
            }}
          >
            View Campaigns
          </Button>
          <Button
            color="primary"
            onClick={() => {
              setShowDraftModal(false);
              navigate(`/campaign/view/${campaignId}`, {
                state: { openPublishModal: true },
              });
            }}
          >
            Publish / Reschedule
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default CampaignForm;
